import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MagnifyingGlass, ArrowsLeftRight, Check, X, Package, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/context/AuthContext";

/**
 * ProductsCompare — pantalla dedicada para comparar dos conjuntos de productos
 * lado a lado. Cada panel tiene su propia búsqueda independiente. Los tiers
 * de precio (mín, reventa, distribuidor, tienda) se editan inline y se guardan
 * con PATCH /products/:id/pricing al perder foco o Enter, sin sobrescribir
 * el resto del producto.
 */
export default function ProductsCompare() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasRole(user, "super_admin") || hasRole(user, "inventory");
  const canSeeCost = hasRole(user, "super_admin") || hasRole(user, "inventory");

  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  // Local update propagator: when a row is edited in one panel and the same
  // product happens to be listed in the other panel, keep both in sync.
  const [productsL, setProductsL] = useState([]);
  const [productsR, setProductsR] = useState([]);

  const applyPatch = (pid, patch) => {
    setProductsL((arr) => arr.map((p) => (p.id === pid ? { ...p, ...patch } : p)));
    setProductsR((arr) => arr.map((p) => (p.id === pid ? { ...p, ...patch } : p)));
  };

  return (
    <div className="p-6 sm:p-8 space-y-5">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate("/products")}
            className="text-xs text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1 mb-2"
            data-testid="back-to-products"
          >
            <ArrowLeft size={13} weight="bold" /> Volver a Productos
          </button>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">CATÁLOGO · COMPARADOR</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <ArrowsLeftRight size={32} weight="duotone" className="text-[#002FA7]" />
            Comparador de productos
          </h1>
          <p className="text-zinc-500 mt-1 text-sm max-w-2xl">
            Busca dos conjuntos de productos de forma independiente (por ejemplo,
            <span className="font-semibold"> i5 7ma gen</span> a la izquierda vs
            <span className="font-semibold"> i5 8va gen</span> a la derecha) y ajusta
            precios inline. Los cambios se guardan automáticamente.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ComparePanel
          side="A"
          categories={categories}
          products={productsL}
          setProducts={setProductsL}
          canEdit={canEdit}
          canSeeCost={canSeeCost}
          onPatched={applyPatch}
          accentClass="border-l-4 border-l-[#002FA7]"
        />
        <ComparePanel
          side="B"
          categories={categories}
          products={productsR}
          setProducts={setProductsR}
          canEdit={canEdit}
          canSeeCost={canSeeCost}
          onPatched={applyPatch}
          accentClass="border-l-4 border-l-emerald-500"
        />
      </div>
    </div>
  );
}

function ComparePanel({ side, categories, products, setProducts, canEdit, canSeeCost, onPatched, accentClass }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [stockMin, setStockMin] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q, category: cat };
      if (stockMin !== "") params.stock_min = Number(stockMin);
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } catch (err) {
      toast.error("Error cargando productos");
    } finally {
      setLoading(false);
    }
  };

  // Debounced load so typing is snappy but doesn't hammer the API.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, cat, stockMin]);

  const total = products.length;

  return (
    <section className={`bg-white border border-zinc-200 ${accentClass} rounded-md flex flex-col`} data-testid={`compare-panel-${side}`}>
      <header className="p-3 border-b border-zinc-200 bg-zinc-50/60">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-xs font-mono-data tracking-widest text-zinc-500">
            LADO {side} · <span className="text-zinc-700 font-semibold">{total}</span> resultado{total === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px] space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Búsqueda</label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Ej. i5 8va — lado ${side}`}
                className="pl-8 h-9 bg-white"
                data-testid={`compare-search-${side}`}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Categoría</label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="w-44 h-9 bg-white" data-testid={`compare-cat-${side}`}>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Stock ≥</label>
            <Input
              type="number"
              min="0"
              value={stockMin}
              onChange={(e) => setStockMin(e.target.value)}
              placeholder="0"
              className="w-20 h-9 bg-white"
              data-testid={`compare-stockmin-${side}`}
            />
          </div>
        </div>
      </header>

      <div className="overflow-x-auto flex-1">
        <table className="w-full text-xs" data-testid={`compare-table-${side}`}>
          <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500">
            <tr>
              <th className="text-left px-2 py-2 min-w-[240px]">Producto</th>
              <th className="text-right px-2 py-2">Stock</th>
              {canSeeCost && <th className="text-right px-2 py-2">Costo</th>}
              {canSeeCost && <th className="text-right px-2 py-2">Mín.</th>}
              <th className="text-right px-2 py-2">Reventa</th>
              <th className="text-right px-2 py-2">Distrib.</th>
              <th className="text-right px-2 py-2">Tienda</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={canSeeCost ? 7 : 5} className="px-2 py-10 text-center text-zinc-400">Cargando…</td></tr>
            )}
            {!loading && products.length === 0 && (
              <tr><td colSpan={canSeeCost ? 7 : 5} className="px-2 py-10 text-center text-zinc-400 dotted-bg">
                <Package size={20} weight="duotone" className="inline mr-1 text-zinc-300" />
                Escribe tu búsqueda para poblar el lado {side}
              </td></tr>
            )}
            {!loading && products.map((p) => (
              <CompareRow
                key={p.id}
                product={p}
                canEdit={canEdit}
                canSeeCost={canSeeCost}
                onPatched={onPatched}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CompareRow({ product, canEdit, canSeeCost, onPatched }) {
  const sc = product.sale_currency || product.currency || "MXN";
  const cc = product.cost_currency || product.currency || "USD";
  const photos = (Array.isArray(product.photos) ? product.photos.filter(Boolean) : []);
  if (photos.length === 0 && product.photos_url) photos.push(product.photos_url);
  const cover = photos[0];
  return (
    <tr className="border-t border-zinc-100 hover:bg-zinc-50 align-top" data-testid={`compare-row-${product.id}`}>
      <td className="px-2 py-2">
        <div className="flex gap-2 items-start">
          {cover ? (
            <a
              href={cover}
              target="_blank"
              rel="noopener noreferrer"
              className="relative shrink-0"
              title={`${photos.length} foto${photos.length === 1 ? "" : "s"} — clic para abrir`}
              data-testid={`compare-photo-${product.id}`}
            >
              <img
                src={cover}
                alt={product.name}
                className="w-12 h-12 object-cover rounded border border-zinc-200"
                loading="lazy"
              />
              {photos.length > 1 && (
                <span className="absolute -top-1 -right-1 bg-[#002FA7] text-white text-[9px] font-bold leading-none rounded-full px-1 py-[1px] min-w-[15px] text-center">
                  {photos.length}
                </span>
              )}
            </a>
          ) : (
            <div className="w-12 h-12 rounded border border-dashed border-zinc-200 shrink-0 flex items-center justify-center text-[9px] text-zinc-300">
              sin foto
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-zinc-900 line-clamp-2 leading-snug" title={product.name}>{product.name}</div>
            {product.characteristics && (
              <div className="text-[10px] text-zinc-500 line-clamp-2 leading-snug mt-0.5" title={product.characteristics}>
                {product.characteristics}
              </div>
            )}
            <div className="text-[10px] text-zinc-400 mt-0.5">
              {product.brand || "—"}{product.sku ? ` · ${product.sku}` : ""}
            </div>
          </div>
        </div>
      </td>
      <td className={`px-2 py-2 text-right numeric font-semibold ${product.stock <= (product.low_stock_threshold || 0) ? "text-[#E0000F]" : "text-zinc-800"}`}>
        {product.stock}
      </td>
      {canSeeCost && (
        <PriceCell product={product} field="cost" currency={cc} canEdit={canEdit} onPatched={onPatched} />
      )}
      {canSeeCost && (
        <PriceCell product={product} field="min_price" currency={sc} canEdit={canEdit} onPatched={onPatched} />
      )}
      <PriceCell product={product} field="reseller_price" currency={sc} canEdit={canEdit} onPatched={onPatched} />
      <PriceCell product={product} field="marketplace_price" currency={sc} canEdit={canEdit} onPatched={onPatched} />
      <PriceCell product={product} field="store_price" currency={sc} canEdit={canEdit} onPatched={onPatched} highlight />
    </tr>
  );
}

function PriceCell({ product, field, currency, canEdit, onPatched, highlight }) {
  const original = Number(product[field] || 0);
  const [val, setVal] = useState(String(original));
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const inputRef = useRef(null);
  const lastSaved = useRef(original);

  // Keep local state in sync if the underlying product prop changes (e.g. after
  // the twin panel edits the same product).
  useEffect(() => {
    if (Number(product[field] || 0) !== Number(val)) {
      setVal(String(Number(product[field] || 0)));
      lastSaved.current = Number(product[field] || 0);
    }
    // eslint-disable-next-line
  }, [product[field]]);

  const commit = async () => {
    const num = Number(val);
    if (isNaN(num) || num < 0) {
      toast.error("Precio inválido");
      setVal(String(lastSaved.current));
      return;
    }
    if (num === lastSaved.current) return; // no-op
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}/pricing`, { [field]: num });
      lastSaved.current = num;
      onPatched?.(product.id, { [field]: num });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando precio");
      setVal(String(lastSaved.current));
    } finally {
      setSaving(false);
    }
  };

  const fmtCur = currency === "USD" ? "$" : "MX$";

  if (!canEdit) {
    return (
      <td className={`px-2 py-2 text-right numeric ${highlight ? "font-bold" : "text-zinc-700"}`}>
        {fmtCur}{Number(product[field] || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
    );
  }

  return (
    <td className={`px-2 py-2 text-right ${highlight ? "bg-[#002FA7]/[0.03]" : ""}`}>
      <div className="relative inline-flex items-center gap-1 justify-end">
        <span className="text-[10px] text-zinc-400">{fmtCur}</span>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); inputRef.current?.blur(); }
            if (e.key === "Escape") { setVal(String(lastSaved.current)); inputRef.current?.blur(); }
          }}
          disabled={saving}
          data-testid={`price-input-${product.id}-${field}`}
          className={`w-24 text-right text-xs px-1.5 py-1 border rounded numeric transition-colors bg-white
            ${highlight ? "border-[#002FA7]/40 focus:border-[#002FA7] font-bold" : "border-zinc-200 focus:border-[#002FA7]"}
            ${saving ? "opacity-60" : ""}
            focus:outline-none focus:ring-1 focus:ring-[#002FA7]/40`}
        />
        {saving ? (
          <span className="text-[10px] text-zinc-400 w-4">…</span>
        ) : savedFlash ? (
          <Check size={12} weight="bold" className="text-emerald-500 w-4" data-testid={`saved-flash-${product.id}-${field}`} />
        ) : (
          <span className="w-4" />
        )}
      </div>
    </td>
  );
}
